import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { DcRuntimeApi } from '@api/bff/openapi/endpoints/dc-runtime-api';
import type { DcRuntimeViewModel } from '@api/bff/openapi/models';
import { bffApiClient } from '@api/bff/client';
import { db } from '@db/database';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useUserStore } from '@stores/userStore';
import { dcRuntimeKeys } from '../constants';

type UseDcRuntimeByWoDetailGlobalIdParams = {
  woDetailGlobalId: string;
  enabled?: boolean;
};

const getCachedRuntime = async (woDetailGlobalId: string) => {
  const cached = await db.cachedCalibrationRuntimes
    .where('woDetailGlobalId')
    .equals(woDetailGlobalId)
    .first();
  return cached ?? null;
};

export const useDcRuntimeByWoDetailGlobalId = ({
  woDetailGlobalId,
  enabled = true
}: UseDcRuntimeByWoDetailGlobalIdParams) => {
  const isOnline = useOnlineStatus();
  const userName = useUserStore(state => state.userName);
  const dcRuntimeApi = new DcRuntimeApi(undefined, undefined, bffApiClient);

  return useQuery<DcRuntimeViewModel | null, Error>({
    queryKey: dcRuntimeKeys.byWoDetailGlobalId(woDetailGlobalId),
    queryFn: async () => {
      // When offline, go straight to cache
      if (!isOnline) {
        return getCachedRuntime(woDetailGlobalId);
      }

      try {
        const response =
          await dcRuntimeApi.apiDcRuntimeByWoDetailWoDetailGlobalIdGet({
            woDetailGlobalId
          });

        // Write-back to IndexedDB to keep cache in sync
        if (response.data) {
          try {
            const existing = await db.cachedCalibrationRuntimes
              .where('woDetailGlobalId')
              .equals(woDetailGlobalId)
              .first();
            if (existing) {
              await db.cachedCalibrationRuntimes.update(existing.id!, {
                ...response.data,
                cachedAt: new Date().toISOString(),
                _userId: existing._userId
              });
            } else {
              // No cached record — insert so offline toggle has data
              await db.cachedCalibrationRuntimes.put({
                ...response.data,
                cachedAt: new Date().toISOString(),
                _userId: userName || 'SYSTEM'
              });
            }
          } catch {
            // Non-critical — don't fail the query for cache update issues
          }
        }

        return response.data;
      } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 204) {
            return null;
          }
        }

        // Network error - try cache as fallback
        const cached = await getCachedRuntime(woDetailGlobalId);
        if (cached) return cached;

        throw error;
      }
    },
    enabled: enabled && !!woDetailGlobalId,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    retry: isOnline ? 3 : false
  });
};
